import { ContainerType } from '../types/database'
import { Aircraft, Transaction, Event } from '../types/intellijetnet'

/**
 * Detect which container an aircraft is currently in based on various data sources
 */
export function detectAircraftContainer(
  aircraft: Aircraft,
  transactions?: Transaction[],
  events?: Event[],
  urlPath?: string
): ContainerType {
  
  // First, try to detect from URL path if available
  if (urlPath) {
    if (urlPath.includes('/transaction/')) {
      return 'sold'
    }
    if (urlPath.includes('/event/')) {
      return 'removed_from_market'
    }
    // If coming from market page, check the container type in URL
    if (urlPath.includes('/market/')) {
      // Default to for_sale for market pages unless we have other indicators
      // This will be refined by the logic below
    }
  }

  // Check if aircraft has recent sold transactions
  if (transactions && transactions.length > 0) {
    const recentTransactions = transactions.filter(t => {
      const transDate = new Date(t.trans_date)
      const now = new Date()
      const daysDiff = (now.getTime() - transDate.getTime()) / (1000 * 60 * 60 * 24)
      return daysDiff <= 365 // Within last year
    })

    if (recentTransactions.length > 0) {
      return 'sold'
    }
  }

  // Check if aircraft has recent removal events
  if (events && events.length > 0) {
    const recentEvents = events.filter(e => {
      const eventDate = new Date(e.date)
      const now = new Date()
      const daysDiff = (now.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24)
      return daysDiff <= 365 // Within last year
    })

    if (recentEvents.length > 0) {
      return 'removed_from_market'
    }
  }

  // Check aircraft status fields
  if (aircraft.forsale === false) {
    // If explicitly not for sale, could be sold or removed
    // Need more context to determine which
    if (aircraft.lifecycle === 'Sold' || aircraft.lifecycle === 'sold') {
      return 'sold'
    }
    return 'removed_from_market'
  }

  // Default to for_sale if no other indicators
  return 'for_sale'
}

/**
 * Get container display name
 */
export function getContainerDisplayName(container: ContainerType): string {
  switch (container) {
    case 'for_sale':
      return 'For Sale'
    case 'sold':
      return 'Sold'
    case 'removed_from_market':
      return 'Removed from Market'
    default:
      return container
  }
}

/**
 * Get container from URL path
 */
export function getContainerFromUrl(pathname: string): ContainerType | null {
  if (pathname.includes('/transaction/')) {
    return 'sold'
  }
  if (pathname.includes('/event/')) {
    return 'removed_from_market'
  }
  if (pathname.includes('/aircraft/')) {
    // Aircraft detail page - need to determine from context
    return null
  }
  return null
}

/**
 * Determine if aircraft can be moved from current container
 */
export function canMoveFromContainer(_container: ContainerType): boolean {
  // All containers allow movement, but with different restrictions
  return true
}

/**
 * Get available target containers for movement
 */
export function getAvailableTargetContainers(currentContainer: ContainerType): ContainerType[] {
  switch (currentContainer) {
    case 'for_sale':
      return ['sold', 'removed_from_market']
    case 'sold':
      return ['for_sale', 'removed_from_market']
    case 'removed_from_market':
      return ['for_sale', 'sold']
    default:
      return []
  }
}
